import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Box, Typography, Paper, Button, Alert, Snackbar } from '@mui/material';
import type { Employee, EmployeeTabProps } from '../../types/employee';
import { useEmployeeData, useEmployeeActions, useWorkScheduleData, useEmployeePayroll } from '../../hooks';
import EmployeeForm from './EmployeeForm';
import IncreaseHourlyRateBottomSheet from './IncreaseHourlyRateBottomSheet';
import HourlyRateHistoryDialog from './HourlyRateHistoryDialog';
import { EmployeeToolbar } from './EmployeeToolbar';
import { EmployeeSpreadsheet } from './EmployeeSpreadsheet';
import { EmployeeContextMenu } from './EmployeeContextMenu';
import EmployeeNonRegularCards from './EmployeeNonRegularCards';
import EmployeePayDrawer from './EmployeePayDrawer';
import { useEmployeeSpreadsheet, useEmployeeInlineEdit } from './hooks';
import type { EmployeeSpreadsheetCellField } from './types';
import { isRegularEmployee } from '../../types/employee';
import DeleteRetiredEmployeeDialog from './DeleteRetiredEmployeeDialog';
import SectionHeader from '../SectionHeader';
import { EmptyState } from '@/components/ui';

const EmployeeTab: React.FC<EmployeeTabProps> = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'retired'>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [increaseRateBottomSheetOpen, setIncreaseRateBottomSheetOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteRetiredDialogOpen, setDeleteRetiredDialogOpen] = useState(false);
  const [deleteRetiredTarget, setDeleteRetiredTarget] = useState<Employee | null>(null);
  const [payDrawerOpen, setPayDrawerOpen] = useState(false);
  const [payDrawerEmployee, setPayDrawerEmployee] = useState<Employee | null>(null);
  const preserveSelectionRef = useRef(false);

  const currentMonth = useMemo(() => new Date(), []);
  const { employees, loading, error } = useEmployeeData();
  const { schedules } = useWorkScheduleData(currentMonth);
  const { monthlyInfoList } = useEmployeePayroll(employees, schedules, currentMonth);
  const { deleteRetiredEmployee, updateEmployee } = useEmployeeActions();

  const {
    filteredEmployees,
    sortedEmployees,
    employeeStats,
    employeeSeveranceInfo,
  } = useEmployeeSpreadsheet({
    employees,
    schedules,
    searchTerm,
    filterStatus,
  });

  const {
    editingCell,
    setEditingCell,
    handleSave,
    cancelEdit,
  } = useEmployeeInlineEdit({
    employees,
    updateEmployee,
    onSuccess: (msg) => setSnackbar({ open: true, message: msg, severity: 'success' }),
    onError: (msg) => setSnackbar({ open: true, message: msg, severity: 'error' }),
  });

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, employee: Employee) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmployee(employee);
  }, []);

  const handleMenuClose = useCallback(() => {
    if (!preserveSelectionRef.current) setSelectedEmployee(null);
    preserveSelectionRef.current = false;
    setAnchorEl(null);
  }, []);

  const handleEmployeeDialogOpen = useCallback((employee?: Employee) => {
    setSelectedEmployee(employee || null);
    setEmployeeDialogOpen(true);
    preserveSelectionRef.current = true;
    setAnchorEl(null);
  }, []);

  const handleEmployeeDialogClose = useCallback(() => {
    setEmployeeDialogOpen(false);
    setSelectedEmployee(null);
    preserveSelectionRef.current = false;
  }, []);

  const handleEmployeeRetire = useCallback(
    async (employee: Employee) => {
      if (!window.confirm(`${employee.name} 직원을 퇴직 처리하시겠습니까?`)) return;
      try {
        await updateEmployee(employee.id, { isRetired: true });
        handleMenuClose();
      } catch (err) {
        console.error('직원 퇴직 처리 실패:', err);
        alert('퇴직 처리에 실패했습니다.');
      }
    },
    [updateEmployee, handleMenuClose]
  );

  const handleEmployeeRestore = useCallback(
    async (employee: Employee) => {
      try {
        await updateEmployee(employee.id, { isRetired: false });
        handleMenuClose();
      } catch (err) {
        console.error('직원 복원 실패:', err);
        alert('직원 복원에 실패했습니다.');
      }
    },
    [updateEmployee, handleMenuClose]
  );

  const handleEmployeeDeleteRequest = useCallback((employee: Employee) => {
    preserveSelectionRef.current = true;
    setDeleteRetiredTarget(employee);
    setDeleteRetiredDialogOpen(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleDeleteRetiredDialogClose = useCallback(() => {
    setDeleteRetiredDialogOpen(false);
    setDeleteRetiredTarget(null);
    preserveSelectionRef.current = false;
  }, []);

  const handleDeleteRetiredConfirm = useCallback(
    async (deleteWorkRecords: boolean) => {
      if (!deleteRetiredTarget) return;
      try {
        await deleteRetiredEmployee(deleteRetiredTarget.id, deleteWorkRecords);
        setSnackbar({
          open: true,
          message: deleteWorkRecords
            ? `${deleteRetiredTarget.name} 직원과 근무 기록이 삭제되었습니다.`
            : `${deleteRetiredTarget.name} 직원 정보가 삭제되었습니다. 근무 기록은 캘린더에 유지됩니다.`,
          severity: 'success',
        });
        handleDeleteRetiredDialogClose();
      } catch (err) {
        console.error('[퇴직자삭제] 실패:', err);
        setSnackbar({
          open: true,
          message: '퇴직자 정보 삭제 중 오류가 발생했습니다.',
          severity: 'error',
        });
      }
    },
    [deleteRetiredEmployee, deleteRetiredTarget, handleDeleteRetiredDialogClose],
  );

  const handleStartEdit = useCallback(
    (employeeId: string, field: EmployeeSpreadsheetCellField) => {
      setEditingCell({ type: 'data', employeeId, field });
    },
    [setEditingCell]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? sortedEmployees.map((e) => e.id) : []);
    },
    [sortedEmployees]
  );

  const handleSelectEmployee = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const regularEmployees = useMemo(
    () => sortedEmployees.filter((e) => isRegularEmployee(e)),
    [sortedEmployees],
  );

  const nonRegularEmployees = useMemo(
    () => sortedEmployees.filter((e) => !isRegularEmployee(e)),
    [sortedEmployees],
  );

  const handlePayCardClick = useCallback((employee: Employee) => {
    setPayDrawerEmployee(employee);
    setPayDrawerOpen(true);
  }, []);

  const payDrawerInfo = useMemo(
    () => monthlyInfoList.find((i) => i.employee.id === payDrawerEmployee?.id) ?? null,
    [monthlyInfoList, payDrawerEmployee],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 2 }}>
        <Typography variant="h6" color="text.secondary">
          직원 데이터를 불러오는 중...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          직원 데이터를 불러오는데 실패했습니다: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <EmployeeToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterStatus={filterStatus}
        onFilterStatusChange={setFilterStatus}
        totalCount={employeeStats.totalCount}
        totalMP={employeeStats.totalMP}
        hourlyCount={employeeStats.hourlyCount}
        salaryCount={employeeStats.salaryCount}
        filteredCount={filteredEmployees.length}
        onAddClick={() => handleEmployeeDialogOpen()}
      />

      {filteredEmployees.length === 0 ? (
        <Paper>
          <EmptyState
            title={
              filterStatus === 'retired'
                ? '퇴직한 직원이 없습니다.'
                : employees.length === 0
                ? '등록된 직원이 없습니다.'
                : '검색 조건에 맞는 직원이 없습니다.'
            }
            action={
              <Button variant="contained" onClick={() => handleEmployeeDialogOpen()}>
                {employees.length === 0 ? '첫 번째 직원 추가하기' : '직원 추가'}
              </Button>
            }
          />
        </Paper>
      ) : (
        <>
          <EmployeeNonRegularCards
            employees={nonRegularEmployees}
            monthlyInfoList={monthlyInfoList}
            onCardClick={handlePayCardClick}
          />

          {regularEmployees.length > 0 && (
            <>
              {nonRegularEmployees.length > 0 && (
                <SectionHeader eyebrow="급여 유형" title="월급제" size="card" />
              )}
              <EmployeeSpreadsheet
                employees={regularEmployees}
                employeeSeveranceInfo={employeeSeveranceInfo}
                selectedIds={selectedIds.filter((id) => regularEmployees.some((e) => e.id === id))}
                onSelectAll={(checked) =>
                  setSelectedIds(
                    checked
                      ? [...new Set([...selectedIds, ...regularEmployees.map((e) => e.id)])]
                      : selectedIds.filter((id) => !regularEmployees.some((e) => e.id === id)),
                  )
                }
                onSelectEmployee={handleSelectEmployee}
                editingCell={editingCell}
                onStartEdit={handleStartEdit}
                onSave={handleSave}
                onCancelEdit={cancelEdit}
                onMenuOpen={handleMenuOpen}
              />
            </>
          )}

          {regularEmployees.length === 0 && nonRegularEmployees.length > 0 && (
            <Typography variant="body2" sx={{ color: 'var(--fg-tertiary)', textAlign: 'center', py: 2 }}>
              정규 직원(월급·시급)은 스프레드시트에서 관리됩니다. 위 카드를 눌러 급여를 확인하세요.
            </Typography>
          )}
        </>
      )}

      <EmployeeContextMenu
        anchorEl={anchorEl}
        selectedEmployee={selectedEmployee}
        onClose={handleMenuClose}
        onEdit={handleEmployeeDialogOpen}
        onIncreaseRate={(emp) => {
          preserveSelectionRef.current = true;
          handleMenuClose();
          setIncreaseRateBottomSheetOpen(true);
        }}
        onHistory={(emp) => {
          preserveSelectionRef.current = true;
          handleMenuClose();
          setHistoryDialogOpen(true);
        }}
        onRetire={handleEmployeeRetire}
        onRestore={handleEmployeeRestore}
        onDelete={handleEmployeeDeleteRequest}
      />

      <EmployeePayDrawer
        open={payDrawerOpen}
        onClose={() => {
          setPayDrawerOpen(false);
          setPayDrawerEmployee(null);
        }}
        employee={payDrawerEmployee}
        monthlyInfo={payDrawerInfo}
        onEdit={(emp) => {
          setPayDrawerOpen(false);
          handleEmployeeDialogOpen(emp);
        }}
      />

      <DeleteRetiredEmployeeDialog
        open={deleteRetiredDialogOpen}
        employee={deleteRetiredTarget}
        onClose={handleDeleteRetiredDialogClose}
        onConfirm={handleDeleteRetiredConfirm}
      />

      <EmployeeForm open={employeeDialogOpen} onClose={handleEmployeeDialogClose} employee={selectedEmployee} />

      {selectedEmployee && (
        <IncreaseHourlyRateBottomSheet
          open={increaseRateBottomSheetOpen}
          onClose={() => {
            setIncreaseRateBottomSheetOpen(false);
            setSelectedEmployee(null);
          }}
          employee={selectedEmployee}
          onSuccess={() => {}}
        />
      )}

      {selectedEmployee && (
        <HourlyRateHistoryDialog
          open={historyDialogOpen}
          onClose={() => {
            setHistoryDialogOpen(false);
            setSelectedEmployee(null);
          }}
          employee={selectedEmployee}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        ContentProps={{ sx: { bgcolor: snackbar.severity === 'success' ? 'success.main' : 'error.main' } }}
      />
    </Box>
  );
};

export default EmployeeTab;
